// Admin Roles & Permissions Configuration
//
// The server is the authority: it checks every admin request against the
// same map (ots-server/config/permissions.js) and sends each admin the list
// of permissions they actually hold (`user.permissions`). This file only
// decides what to *show* — menus, pages and buttons — so keep it in step with
// the server. When a user object carries `permissions`, that list wins.

export const ADMIN_ROLES = {
  SUPER_ADMIN: "super_admin",
  MANAGER: "manager",
  REVIEWER: "reviewer",
  FINANCE: "finance",
  SUPPORT: "support",
};

// Roles a super admin can give to other people (super admin never is).
export const ASSIGNABLE_ROLES = [
  ADMIN_ROLES.MANAGER,
  ADMIN_ROLES.REVIEWER,
  ADMIN_ROLES.FINANCE,
  ADMIN_ROLES.SUPPORT,
];

// Accounts created before the role split may still carry the old names.
const LEGACY_ROLE_ALIASES = {
  admin: ADMIN_ROLES.MANAGER,
  moderator: ADMIN_ROLES.REVIEWER,
};

export const normalizeRole = (role) => LEGACY_ROLE_ALIASES[role] || role || null;

export const ROLE_LABELS = {
  [ADMIN_ROLES.SUPER_ADMIN]: "Super Admin",
  [ADMIN_ROLES.MANAGER]: "Manager",
  [ADMIN_ROLES.REVIEWER]: "Reviewer",
  [ADMIN_ROLES.FINANCE]: "Finance",
  [ADMIN_ROLES.SUPPORT]: "Support",
  // Old role names, shown as the role they now map to
  admin: "Manager",
  moderator: "Reviewer",
};

export const ROLE_DESCRIPTIONS = {
  [ADMIN_ROLES.SUPER_ADMIN]: "Full access, including bank details, commission and admin accounts",
  [ADMIN_ROLES.MANAGER]: "Runs the team: approvals, payment checks and reports",
  [ADMIN_ROLES.REVIEWER]: "Reviews and approves owners and properties",
  [ADMIN_ROLES.FINANCE]: "Checks payments and sees revenue",
  [ADMIN_ROLES.SUPPORT]: "Read-only access to users, tenants and agreements",
};

export const ROLE_COLORS = {
  [ADMIN_ROLES.SUPER_ADMIN]: "#e94560",
  [ADMIN_ROLES.MANAGER]: "#6366f1",
  [ADMIN_ROLES.REVIEWER]: "#22c55e",
  [ADMIN_ROLES.FINANCE]: "#f59e0b",
  [ADMIN_ROLES.SUPPORT]: "#0ea5e9",
  admin: "#6366f1",
  moderator: "#22c55e",
};

// Define all available permissions
export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: "view_dashboard",

  // User Management
  VIEW_USERS: "view_users",
  EDIT_USERS: "edit_users",
  DELETE_USERS: "delete_users",

  // Landlord Management
  VIEW_LANDLORDS: "view_landlords",
  APPROVE_LANDLORDS: "approve_landlords",
  REJECT_LANDLORDS: "reject_landlords",

  // Tenant Management
  VIEW_TENANTS: "view_tenants",
  EDIT_TENANTS: "edit_tenants",

  // Property Management
  VIEW_PROPERTIES: "view_properties",
  APPROVE_PROPERTIES: "approve_properties",
  DELETE_PROPERTIES: "delete_properties",

  // Revenue Management
  VIEW_REVENUE: "view_revenue",
  MANAGE_REVENUE: "manage_revenue",

  // Reports
  VIEW_REPORTS: "view_reports",
  EXPORT_REPORTS: "export_reports",

  // Admin Management (Super Admin only)
  VIEW_ADMINS: "view_admins",
  CREATE_ADMINS: "create_admins",
  EDIT_ADMINS: "edit_admins",
  DELETE_ADMINS: "delete_admins",

  // Settings
  VIEW_SETTINGS: "view_settings",
  EDIT_SETTINGS: "edit_settings",

  // Agreements
  VIEW_AGREEMENTS: "view_agreements",
  MANAGE_AGREEMENTS: "manage_agreements",

  // Activity Logs
  VIEW_ACTIVITY_LOGS: "view_activity_logs",

  // Cash bookings: reviewing the OTS service-fee receipt (view AND decide it)
  VERIFY_PAYMENTS: "verify_payments",
  // Narrower slices of the same ground, for splitting the work — never a
  // role default, only ever granted to a specific admin (see
  // Admin Management). A person with only one of these sees a read-only or
  // partial view; VERIFY_PAYMENTS is still the one that can act.
  VIEW_OTS_FEE_RECEIPTS: "view_ots_fee_receipts",
  VIEW_OWNER_FOLLOWUPS: "view_owner_followups",
  VIEW_TENANT_FOLLOWUPS: "view_tenant_followups",

  // Owner payout claims: MANAGE_PAYOUTS sees the queue and gets a claim ready;
  // only APPROVE_PAYOUTS (super admin) releases money.
  MANAGE_PAYOUTS: "manage_payouts",
  APPROVE_PAYOUTS: "approve_payouts",

  // A landlord's HIGH-severity / illegal-activity violation report skips the
  // tenant's normal notice period — an admin has to sign off on it first.
  REVIEW_VIOLATION_ESCALATIONS: "review_violation_escalations",

  // A tenant's property review doesn't go public until an admin approves it.
  MODERATE_REVIEWS: "moderate_reviews",
};

// Permissions per role — a mirror of the server, used only until the server's
// own list for the logged-in admin has arrived.
export const ROLE_PERMISSIONS = {
  [ADMIN_ROLES.SUPER_ADMIN]: Object.values(PERMISSIONS), // All permissions

  [ADMIN_ROLES.MANAGER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.VIEW_LANDLORDS,
    PERMISSIONS.APPROVE_LANDLORDS,
    PERMISSIONS.REJECT_LANDLORDS,
    PERMISSIONS.VIEW_TENANTS,
    PERMISSIONS.EDIT_TENANTS,
    PERMISSIONS.VIEW_PROPERTIES,
    PERMISSIONS.APPROVE_PROPERTIES,
    PERMISSIONS.VIEW_REVENUE,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.VIEW_ACTIVITY_LOGS,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.VIEW_AGREEMENTS,
    PERMISSIONS.VERIFY_PAYMENTS,
    PERMISSIONS.MANAGE_PAYOUTS,
    PERMISSIONS.REVIEW_VIOLATION_ESCALATIONS,
    PERMISSIONS.MODERATE_REVIEWS,
  ],

  [ADMIN_ROLES.REVIEWER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_LANDLORDS,
    PERMISSIONS.APPROVE_LANDLORDS,
    PERMISSIONS.REJECT_LANDLORDS,
    PERMISSIONS.VIEW_TENANTS,
    PERMISSIONS.VIEW_PROPERTIES,
    PERMISSIONS.APPROVE_PROPERTIES,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.VIEW_AGREEMENTS,
    PERMISSIONS.REVIEW_VIOLATION_ESCALATIONS,
    PERMISSIONS.MODERATE_REVIEWS,
  ],

  [ADMIN_ROLES.FINANCE]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_REVENUE,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.VERIFY_PAYMENTS,
    PERMISSIONS.MANAGE_PAYOUTS,
  ],

  [ADMIN_ROLES.SUPPORT]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_TENANTS,
    PERMISSIONS.VIEW_AGREEMENTS,
  ],
};

// Helper function to check if a role has a specific permission
export const hasPermission = (role, permission) => {
  const list = ROLE_PERMISSIONS[normalizeRole(role)];
  return Boolean(list && list.includes(permission));
};

// Helper function to get all permissions for a role
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[normalizeRole(role)] || [];
};

/**
 * What one logged-in admin may do. Non-admins and accounts without a role get
 * nothing. The server's list (`user.permissions`) is used when it is there;
 * the role map above covers the moment before it has loaded.
 */
export const getUserPermissions = (user) => {
  if (!user || user.type !== "admin" || !user.adminRole) return [];
  if (Array.isArray(user.permissions)) return user.permissions;
  return getRolePermissions(user.adminRole);
};

export const userCan = (user, permission) =>
  getUserPermissions(user).includes(permission);

// Every admin page, with the permission it needs. The sidebar, the route
// guard and the post-login redirect all read this one list, so a role only
// ever sees, and lands on, pages it can open.
export const ADMIN_NAV_ITEMS = [
  { title: "Dashboard", path: "/admin-dashboard", permission: PERMISSIONS.VIEW_DASHBOARD },
  { title: "All Users", path: "/admin/users", permission: PERMISSIONS.VIEW_USERS },
  { title: "Landlords", path: "/admin/landlords", permission: PERMISSIONS.VIEW_LANDLORDS },
  { title: "Tenants", path: "/admin/tenants", permission: PERMISSIONS.VIEW_TENANTS },
  { title: "Properties", path: "/admin/properties", permission: PERMISSIONS.VIEW_PROPERTIES },
  { title: "Revenue", path: "/admin/revenue", permission: PERMISSIONS.VIEW_REVENUE },
  { title: "Owner Payouts", path: "/admin/payouts", permission: PERMISSIONS.MANAGE_PAYOUTS },
  { title: "OTS Fee Receipts", path: "/admin/ots-fee-receipts", permission: PERMISSIONS.VERIFY_PAYMENTS },
  { title: "Follow-ups", path: "/admin/tenant-funnel", permission: PERMISSIONS.VERIFY_PAYMENTS },
  { title: "Move-outs & Dues", path: "/admin/move-outs", permission: PERMISSIONS.VIEW_PROPERTIES },
  { title: "Violation Escalations", path: "/admin/violation-escalations", permission: PERMISSIONS.REVIEW_VIOLATION_ESCALATIONS },
  { title: "Property Reviews", path: "/admin/property-reviews", permission: PERMISSIONS.MODERATE_REVIEWS },
  { title: "Reports", path: "/admin/reports", permission: PERMISSIONS.VIEW_REPORTS },
  { title: "Admin Management", path: "/admin/manage-admins", permission: PERMISSIONS.VIEW_ADMINS },
  { title: "Activity Logs", path: "/admin/activity-logs", permission: PERMISSIONS.VIEW_ACTIVITY_LOGS },
  { title: "Settings", path: "/admin/settings", permission: PERMISSIONS.VIEW_SETTINGS },
  // Bank account and commission: the server only lets a super admin save here.
  { title: "Commission Settings", path: "/admin/commission-settings", permission: PERMISSIONS.EDIT_SETTINGS },
];

export const getPermissionForPath = (path) =>
  ADMIN_NAV_ITEMS.find((item) => item.path === path)?.permission || null;

export const getVisibleNavItems = (user) =>
  ADMIN_NAV_ITEMS.filter((item) => userCan(user, item.permission));

// Where an admin should land: the dashboard if they have it, otherwise the
// first page their role opens. Null when their role opens nothing.
export const getAdminHomePath = (user) => getVisibleNavItems(user)[0]?.path || null;
